import { useMemo, useState, useCallback, useEffect } from "react";
import { Player } from "@remotion/player";
import { buildSubtitleReorganizeOptions } from "@/lib/subtitles/buildSubtitleReorganizeOptions";
import SubtitlePresetComposition from "@/components/remotion/SubtitlePresetComposition";
import type { PresetChange } from "@/components/remotion/SubtitlePresetComposition";
import { useMyFontsQuery } from "@/generated/graphql";
import type { MyBrandKitsQuery } from "@/generated/graphql";
import { readKitSubtitleTextTransform } from "@/lib/subtitles/subtitleTextTransform";
import { subtitleOutlineFromKit, subtitleOutlineLineStyle } from "@/lib/subtitles/subtitleOutline";
import { parsePersistedPostLayout } from "@/lib/layouts/persistPostLayout";
import { circuloCurveFromLayout } from "@/lib/layouts/resolveCirculoPreset";

type BrandKit = MyBrandKitsQuery["myBrandKits"][number];

// Wrapper component for Remotion Player typing
const AnimationPreview: React.FC<any> = (props) => {
  return <SubtitlePresetComposition {...props} />;
};

interface BrandKitSubtitlePreviewProps {
  brandKit: BrandKit | null | undefined;
  className?: string;
  small?: boolean;
}

export default function BrandKitSubtitlePreview({
  brandKit,
  className,
  small,
}: BrandKitSubtitlePreviewProps) {
  const [loadedFonts, setLoadedFonts] = useState<Set<string>>(new Set());
  const { data: fontsData } = useMyFontsQuery();

  // Font loading helper (same pattern as BrandKitsNew.tsx)
  const loadFont = useCallback(
    (fontId: string) => {
      if (!fontsData?.myFonts || loadedFonts.has(fontId)) return;

      const font = fontsData.myFonts.find((f) => f.id === fontId);
      if (!font || !font.file_url) return;

      const fontFamilyName = fontId;
      const fontFaceRule = `
        @font-face {
          font-family: '${fontFamilyName}';
          src: url('${font.file_url}') format('truetype');
          font-display: swap;
        }
      `;

      let styleElement = document.getElementById(
        `font-loader-${fontId}`
      ) as HTMLStyleElement;
      if (!styleElement) {
        styleElement = document.createElement("style");
        styleElement.id = `font-loader-${fontId}`;
        styleElement.textContent = fontFaceRule;
        document.head.appendChild(styleElement);
      }

      setLoadedFonts((prev) => new Set([...prev, fontId]));
    },
    [fontsData?.myFonts, loadedFonts]
  );

  // Load font when brand kit changes
  useEffect(() => {
    if (brandKit?.data?.subtitle?.subtitle_font_id) {
      loadFont(brandKit.data.subtitle.subtitle_font_id);
    }
  }, [brandKit?.data?.subtitle?.subtitle_font_id, loadFont]);

  // Convert brand kit subtitle data to preset format
  const preset: PresetChange[] = useMemo(() => {
    if (!brandKit?.data?.subtitle) return [];
    const s = brandKit.data.subtitle;

    const fontToUse =
      s.subtitle_font_id && loadedFonts.has(s.subtitle_font_id)
        ? s.subtitle_font_id
        : undefined;

    return [
      { type: "set-value", attribute: "animation", value: s.animation },
      {
        type: "set-value",
        attribute: "lineStyle.fontName",
        value: fontToUse,
      },
      { type: "set-value", attribute: "lineStyle.fontSize", value: small ? 14 : 12 },
      {
        type: "set-value",
        attribute: "lineStyle.fontColor",
        value: s.subtitle_color,
      },
      {
        type: "set-value",
        attribute: "lineStyle.textTransform",
        value: readKitSubtitleTextTransform(s),
      },
      {
        type: "set-value",
        attribute: "highlightStyle.fontColor",
        value: s.subtitle_highlight_color || "#0DFDE8",
      },
      {
        type: "set-value",
        attribute: "lineStyle.highlightCurrentWord",
        value: s.subtitle_highlight_enabled,
      },
      {
        type: "set-value",
        attribute: "lineStyle.shadowSize",
        value: s.shadow_enabled
          ? Math.round((s.shadow_size / 100) * 20)
          : 0,
      },
      {
        type: "set-value",
        attribute: "lineStyle.shadowColor",
        value: s.shadow_color,
      },
      ...Object.entries(subtitleOutlineLineStyle(subtitleOutlineFromKit(s)))
        .map(([key, value]): PresetChange => ({ type: "set-value", attribute: `lineStyle.${key}`, value })),
      {
        type: "set-value",
        attribute: "lineStyle.backgroundColor",
        value: s.background_color,
      },
      {
        type: "set-value",
        attribute: "lineStyle.enableBackground",
        value: s.background_enabled,
      },
      {
        type: "set-value",
        attribute: "highlightBoxStyle.enabled",
        value: s.highlight_box_enabled ?? false,
      },
      {
        type: "set-value",
        attribute: "highlightBoxStyle.backgroundColor",
        value: s.highlight_box_background_color || "#4023d1",
      },
      {
        type: "set-value",
        attribute: "highlightBoxStyle.borderRadius",
        value: s.highlight_box_border_radius ?? 0.2,
      },
      {
        type: "set-value",
        attribute: "highlightBoxStyle.paddingX",
        value: s.highlight_box_padding_x ?? 0.3,
      },
      {
        type: "set-value",
        attribute: "highlightBoxStyle.paddingY",
        value: s.highlight_box_padding_y ?? 0.05,
      },
      {
        type: "reorganize-words",
        settings: buildSubtitleReorganizeOptions({
          maxWordsPerLine: s.max_words_per_line ?? 4,
          removePunctuation:
            "remove_punctuation" in s
              ? Boolean((s as { remove_punctuation?: boolean }).remove_punctuation)
              : false,
        }),
      },
    ];
  }, [brandKit, loadedFonts]);

  const curve = useMemo(
    () => circuloCurveFromLayout(
      parsePersistedPostLayout(brandKit?.data?.post_layout)?.postLayout,
    ),
    [brandKit?.data?.post_layout],
  );

  // Compute logo overlay style from brand kit logo data
  const logoStyle = useMemo((): React.CSSProperties | null => {
    const logo = brandKit?.data?.logo;
    if (!logo?.logo_image_url) return null;

    const size = logo.logo_size || 20;
    const opacity = (logo.logo_opacity ?? 70) / 100;
    const hMargin = logo.logo_horizontal_margin || 5;
    const vMargin = logo.logo_vertical_margin || 5;
    const position = logo.logo_position || "bottom-right";

    const style: React.CSSProperties = {
      position: "absolute",
      width: `${size}%`,
      maxHeight: `${size}%`,
      objectFit: "contain",
      opacity,
      pointerEvents: "none",
    };

    // Parse position string (e.g. "bottom-right", "top-left", "center")
    const parts = position === "center" ? ["center", "center"] : position.split("-");
    const yPos = parts[0] || "bottom";
    const xPos = parts[1] || "right";

    if (xPos === "left") style.left = `${hMargin}%`;
    else if (xPos === "center") { style.left = "50%"; style.transform = "translateX(-50%)"; }
    else style.right = `${hMargin}%`;

    if (yPos === "top") style.top = `${vMargin}%`;
    else if (yPos === "center") {
      style.top = "50%";
      style.transform = style.transform ? `${style.transform} translateY(-50%)` : "translateY(-50%)";
    } else style.bottom = `${vMargin}%`;

    return style;
  }, [brandKit?.data?.logo]);

  const hasSubtitle = !!brandKit?.data?.subtitle;
  const logoUrl = brandKit?.data?.logo?.logo_image_url;

  if (!hasSubtitle && !logoUrl) return null;

  return (
    <div
      className={`${small ? "" : "mt-2 border border-border"} rounded-lg overflow-hidden bg-black ${className || ""}`}
    >
      <div className="relative" style={small ? { height: 60 } : { aspectRatio: "16/9" }}>
        {hasSubtitle && (
          <Player
            key={`preview-${brandKit!.id}-${loadedFonts.size}`}
            component={AnimationPreview}
            inputProps={{ preset, curve }}
            durationInFrames={90}
            fps={30}
            compositionWidth={160}
            compositionHeight={90}
            style={{ width: "100%", height: "100%" }}
            controls={false}
            loop
            autoPlay
          />
        )}
        {logoUrl && logoStyle && (
          <img
            src={logoUrl}
            alt=""
            crossOrigin="anonymous"
            style={logoStyle}
          />
        )}
      </div>
    </div>
  );
}
